import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Wrench, Loader2, Save } from "lucide-react";

export const MaintenanceToggle = () => {
  const { t, refreshSettings } = useApp();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => setS({ maintenance: false })); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", patch);
      setS(data);
      refreshSettings();
      toast.success(patch.maintenance === undefined ? t.admin.maintSaved : data.maintenance ? t.admin.maintOn : t.admin.maintOff);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return null;
  return (
    <section className={`glass rounded-2xl p-5 mb-8 sm:mb-10 border ${s.maintenance ? "border-rose-500/40 bg-rose-500/5" : "border-white/10"}`} data-testid="maintenance-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl grid place-items-center border ${s.maintenance ? "border-rose-400/50 bg-rose-500/15 text-rose-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}><Wrench size={18} /></div>
          <div>
            <h2 className="text-lg text-amber-100 font-medium">{t.admin.maintTitle}</h2>
            <p className="text-xs text-slate-400">{t.admin.maintSub}</p>
          </div>
        </div>
        <button onClick={() => save({ maintenance: !s.maintenance })} disabled={busy} data-testid="maintenance-toggle-btn" role="switch" aria-checked={s.maintenance}
          className={`relative inline-flex h-9 w-[4.5rem] items-center rounded-full transition-colors border ${s.maintenance ? "bg-rose-500/80 border-rose-400" : "bg-white/10 border-white/20"} disabled:opacity-60`}>
          <span className={`absolute left-1 h-7 w-7 rounded-full bg-white shadow transition-transform ${s.maintenance ? "translate-x-9" : "translate-x-0"}`} />
          <span className={`absolute text-[10px] font-bold ${s.maintenance ? "left-2 text-white" : "right-2 text-slate-300"}`}>{s.maintenance ? "ON" : "OFF"}</span>
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 mt-4">
        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">{t.admin.maintMsgHi}</span>
          <textarea data-testid="maintenance-msg-hi" rows={2} value={s.maintenance_message_hi || ""} onChange={(e) => setS({ ...s, maintenance_message_hi: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400/50" />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">{t.admin.maintMsgEn}</span>
          <textarea data-testid="maintenance-msg-en" rows={2} value={s.maintenance_message_en || ""} onChange={(e) => setS({ ...s, maintenance_message_en: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400/50" />
        </label>
      </div>
      <button onClick={() => save({ maintenance_message_hi: s.maintenance_message_hi, maintenance_message_en: s.maintenance_message_en })} disabled={busy} data-testid="maintenance-save-btn"
        className="btn-ghost-gold rounded-full px-4 py-2 mt-3 text-sm flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t.admin.save}
      </button>
      {s.maintenance && <p className="text-xs text-rose-300 mt-3" data-testid="maintenance-warning">{t.admin.maintWarning}</p>}
    </section>
  );
};
